import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	Inject,
	NgZone,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	ViewChild,
	effect,
	signal
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { IssuesComponent } from '@sp-client/app/components/issues/issues.component';
import { NotificationComponent } from '@sp-client/app/components/notification/notification.component';
import { ToastComponent } from '@sp-client/app/components/toast/toast.component';
import { SidebarMenu } from '@sp-client/app/interfaces/sidebar.interface';
import { cm } from '@sp-shared';
import { filter } from 'rxjs/operators';
import { SidePanelComponent } from './components/side-panel/side-panel.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { TabbarComponent } from './components/tabbar/tabbar.component';
import { RouteNames, Routes } from './enums/routes.enum';
import { ConfigService } from './services/config.service';
import { CountryGuideService } from './services/country-guide.service';
import { DayTripsService } from './services/day-trips.service';
import { FavoritesService } from './services/favorites.service';
import { GuidesService } from './services/guides.service';
import { IssueService } from './services/issue.service';
import { ItineraryService } from './services/itinerary.service';
import { OnboardService } from './services/onboard.service';
import { JournalService } from './services/journal.service';
import { LoginService } from './services/login.service';
import { NavigationService } from './services/navigation.service';
import { NeighborhoodGuideService } from './services/neighborhood-guide.service';
import { PanelRoutingService } from './services/panel-routing.service';
import { PlanService } from './services/plan.service';
import { RouteHistoryService } from './services/route-history.service';
import { RouteSyncService } from './services/route-sync.service';
import { RouteTrackerService } from './services/route-tracker.service';
import { SidePanelService } from './services/side-panel.service';
import { UpgradeModalService } from './services/upgrade-modal.service';
import { UserService } from './services/user.service';
import { VisaService } from './services/visas.service';
import { LogService } from './services/log.service';
import { UpgradeModalComponent } from './components/upgrade-modal/upgrade-modal.component';

@Component({
	imports: [
		CommonModule,
		RouterModule,
		SidebarComponent,
		TabbarComponent,
		ToastComponent,
		IssuesComponent,
		NotificationComponent,
		SidePanelComponent,
		UpgradeModalComponent
	],
	selector: 'sp-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(SidePanelComponent) sidePanel!: SidePanelComponent;
	isMobile = false;
	isTablet = false;
	isTabletWide = false;
	isDesktop = false;
	title = 'Your Scoutly Plans';
	showSidebar = false;
	showTabbar = false;
	sidebarMenu = this.setSidebarMenu();
	isBrowser = false;
	isIOSStandalone = false;
	isAndroidStandlone = false;
	private scrollPositions = new Map<string, number>();
	showNetworkStatus = false;
	showErrorPage = false;
	scrollPosition = 0;
	openPanel = signal(false);
	lastPath: string;
	isLoading = false;
	bypassLoading = signal(false);
	private networkStatusTimeout: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private planService: PlanService,
		private configService: ConfigService,
		private cdRef: ChangeDetectorRef,
		private ngZone: NgZone,
		private navigationService: NavigationService,
		private dayTripService: DayTripsService,
		private neighborhoodGuidesService: NeighborhoodGuideService,
		private visaService: VisaService,
		private journalService: JournalService,
		private userService: UserService,
		private countryGuideService: CountryGuideService,
		private itineraryService: ItineraryService,
		private favoritesService: FavoritesService,
		private guidesService: GuidesService,
		private routeHistoryService: RouteHistoryService,
		private sidePanelService: SidePanelService,
		public upgradeModalService: UpgradeModalService,
		private loginService: LoginService,
		private routeSyncService: RouteSyncService,
		private panelRouteService: PanelRoutingService,
		private issueService: IssueService,
		private routeTracker: RouteTrackerService,
		private onboardService: OnboardService,
		private log: LogService,
		private swUpdate: SwUpdate,
		@Inject(PLATFORM_ID) private platformId: object
	) {
		this.isBrowser = isPlatformBrowser(this.platformId);
		effect(() => {
			this.isMobile = this.configService.isMobileSignal();
			this.isTablet = this.configService.isTabletSignal();
			this.isTabletWide = this.configService.isTabletWideSignal();
			this.isDesktop = this.configService.isDesktopSignal();
		});

		const bypass: string[] = [
			Routes.Login,
			Routes.SignUp,
			Routes.SignUpEmail,
			Routes.ForgotPassword,
			Routes.PasswordReset,
			Routes.Logout,
			Routes.Onboard,
			Routes.OnboardCountry,
			Routes.OnboardRegion,
			Routes.OnboardDemographic,
			Routes.OnboardReview
		];
		// const url = this.router.url;
		this.router.events.pipe(filter(evt => evt instanceof NavigationEnd)).subscribe((evt: NavigationEnd) => {
			const tree = this.router.parseUrl(evt.urlAfterRedirects);
			const url = tree.root.children['primary']?.segments.map(it => it.path).join('/') ?? '';
			const path = '/' + url;
			this.bypassLoading.set(bypass.includes(path));
		});

		effect(() => {
			this.openPanel.set(this.sidePanelService.isOpenSignal());
		});

		effect(() => {
			const failedToken = this.loginService.failedTokenSignal();
			if (failedToken && !this.bypassLoading()) {
				this.panelRouteService.navigate([RouteNames.Login]);
				return;
			}
		});

		//TODO: Update isLoading to a central service or way to not just pivot off of planservice
		effect(() => {
			this.isLoading = this.planService.isLoadingSignal();
			const suppressNetworkStatus = this.issueService.getSuppressNetworkStatusSignal();
			// this.log.debug('this.isLoading', this.isLoading);

			if (this.bypassLoading() || suppressNetworkStatus) {
				if (this.networkStatusTimeout) {
					clearTimeout(this.networkStatusTimeout);
					this.networkStatusTimeout = null;
				}
				this.isLoading = false;
				this.showNetworkStatus = false;
				return;
			}

			if (!this.isLoading) {
				this.showNetworkStatus = false;
				if (this.networkStatusTimeout) {
					clearTimeout(this.networkStatusTimeout);
					this.networkStatusTimeout = null;
				}
				return;
			}

			if (this.networkStatusTimeout) {
				clearTimeout(this.networkStatusTimeout);
			}

			this.ngZone.runOutsideAngular(() => {
				this.networkStatusTimeout = setTimeout(() => {
					if (this.isLoading && !this.issueService.getSuppressNetworkStatusSignal()) {
						this.showNetworkStatus = true;
						this.cdRef.detectChanges();
					}
				}, 5000);
			});
		});

		effect(() => {
			const suppressNetworkStatus = this.issueService.getSuppressNetworkStatusSignal();
			this.showNetworkStatus = suppressNetworkStatus ? false : this.issueService.getNetworkStatusSignal();
			this.showErrorPage = this.issueService.getIssueSignal();
		});

		if (this.isBrowser) {
			this.router.events.subscribe(event => {
				if (event instanceof NavigationStart) {
					this.saveScrollPosition();
				}

				if (event instanceof NavigationEnd) {
					this.restoreScrollPosition(event.urlAfterRedirects);

					const hasPanelOutlet = this.router.routerState.snapshot.root.children.some(r => r.outlet === 'panel');
					this.sidePanelService.setIsOpen(hasPanelOutlet);
				}
			});
		}

		//TODO: check if plan data is able to be pulled back, otherwise show network status and issue
		// setTimeout(() => {
		//   this.showNetworkStatus = true;
		// }, 5000);
	}

	ngOnInit(): void {
		if (this.isBrowser) {
			if ('scrollRestoration' in history) {
				history.scrollRestoration = 'manual';
			}
		}

		if (this.isBrowser && this.swUpdate.isEnabled) {
			this.swUpdate.versionUpdates
				.pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
				.subscribe(event => {
					this.log.warn('New service worker version detected. Reloading app.', {
						currentHash: event.currentVersion.hash,
						latestHash: event.latestVersion.hash
					});

					void this.swUpdate.activateUpdate().then(() => {
						document.location.reload();
					});
				});

			this.swUpdate.unrecoverable.subscribe(event => {
				this.log.error('Service worker entered unrecoverable state. Reloading app.', event.reason);
				document.location.reload();
			});

			void this.swUpdate.checkForUpdate().catch(error => {
				this.log.warn('Service worker update check failed', error);
			});
		}

		if (this.isStandaloneMode()) {
			this.isIOSStandalone = this.configService.isIos();
			this.isAndroidStandlone = this.configService.isAndroid();
		}

		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
			const currentRoute = this.route.firstChild;
			if (currentRoute?.snapshot.data['showSidebar'] !== undefined) {
				this.showSidebar = currentRoute.snapshot.data['showSidebar'];
			} else {
				this.showSidebar = true;
			}
			if (currentRoute?.snapshot.data['showTabbar'] !== undefined) {
				this.showTabbar = currentRoute.snapshot.data['showTabbar'];
			} else {
				this.showTabbar = false;
			}
			this.cdRef.detectChanges();
		});

		// void this.initService;
		void this.loginService;
		void this.itineraryService;
		void this.guidesService;
		void this.userService;
		void this.favoritesService;
		void this.countryGuideService;
		void this.neighborhoodGuidesService;
		void this.dayTripService;
		void this.visaService;
		void this.journalService;
		void this.routeHistoryService;
		void this.sidePanelService;
	}

	ngAfterViewInit() {
		if (!this.isBrowser) return;
		this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
			if (this.isMobile) {
				document.documentElement.scrollTop = 0;
				document.body.scrollTop = 0;
			}
		});

		this.restoreScrollPosition(this.router.url);
	}

	ngOnDestroy(): void {
		if (this.networkStatusTimeout) {
			clearTimeout(this.networkStatusTimeout);
			this.networkStatusTimeout = null;
		}
	}

	handleFinishTimer() {
		// this.showErrorPage = true;
		// this.showNetworkStatus = false;
	}

	private saveScrollPosition(): void {
		if (!this.isBrowser) return;
		if (this.isOnboardUrl(this.router.url)) return;

		if (this.isTablet && cm.isDefinedAndNotNull(this.sidePanel) && this.router.url !== Routes.Root) {
			const position = this.sidePanel.getScrollTop();
			this.scrollPositions.set(this.router.url, position);
		} else {
			const position = Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop);
			this.scrollPositions.set(this.router.url, position);
		}
	}

	private restoreScrollPosition(url: string): void {
		if (!this.isBrowser) return;
		if (this.isItineraryDeepLinkUrl(url)) {
			return;
		}
		if (this.isOnboardUrl(url)) {
			requestAnimationFrame(() => {
				if (this.isTablet && cm.isDefinedAndNotNull(this.sidePanel) && url !== Routes.Root) {
					this.sidePanel.setScrollTop(0);
				}
				const appContent = document.querySelector('.app-content') as HTMLElement | null;
				if (appContent) {
					appContent.scrollTop = 0;
				}
				document.documentElement.scrollTop = 0;
				document.body.scrollTop = 0;
			});
			return;
		}

		const savedPosition = this.scrollPositions.get(url);

		if (this.isTablet && cm.isDefinedAndNotNull(this.sidePanel) && url !== Routes.Root) {
			if (cm.isNotEmpty(savedPosition)) {
				requestAnimationFrame(() => {
					this.sidePanel.setScrollTo(0, savedPosition);
				});
			} else {
				requestAnimationFrame(() => {
					this.sidePanel.setScrollTop(0);
				});
			}
		} else {
			if (cm.isNotEmpty(savedPosition)) {
				requestAnimationFrame(() => {
					document.documentElement.scrollTo(0, savedPosition);
					document.body.scrollTo(0, savedPosition);
				});
			} else {
				requestAnimationFrame(() => {
					document.documentElement.scrollTop = 0;
					document.body.scrollTop = 0;
				});
			}
		}
	}

	private isItineraryDeepLinkUrl(url: string): boolean {
		if (!url.includes('itinerary')) {
			return false;
		}

		return ['itTab=', 'itDay=', 'itActivity=', 'itStop=', 'itActivityOrder=', 'spNavTs='].some(param =>
			url.includes(param)
		);
	}

	private isStandaloneMode(): boolean {
		if (this.isBrowser) {
			return (
				(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
				('standalone' in window.navigator && (window.navigator as any).standalone)
			);
		}
		return false;
	}

	private isOnboardUrl(url: string): boolean {
		const normalizedUrl = url.split('?')[0].split('#')[0];
		return normalizedUrl.startsWith(Routes.Onboard);
	}

	onSelection(event: string) {
		this.log.debug('selection', event);
		if (this.isTablet) {
			this.panelRouteService.panelRoutingControl(event);
		}
	}

	private setSidebarMenu(): SidebarMenu[] {
		return [
			{
				name: 'Dashboard',
				iconFilename: 'dashboard',
				routeLink: '/',
				activeClass: 'active',
				routeLinkActiveOption: true
			},
			{
				name: 'Itinerary',
				iconFilename: 'itinerary',
				routeLink: '/itinerary',
				activeClass: 'active',
				routeLinkActiveOption: false,
				outlet: 'panel'
			},
			{
				name: 'Journal',
				iconFilename: 'journal',
				routeLink: '/journal',
				activeClass: 'active',
				routeLinkActiveOption: false,
				outlet: 'panel'
			},
			{
				name: 'Visa Options',
				iconFilename: 'id-card',
				routeLink: '/visas',
				activeClass: 'active',
				routeLinkActiveOption: false,
				outlet: 'panel'
			},
			{
				name: 'Favorites',
				iconFilename: 'bookmark',
				routeLink: '/favorites',
				activeClass: 'active',
				routeLinkActiveOption: false,
				outlet: 'panel'
			}
		];
	}
}
